import {
	AfterViewInit,
	Component,
	EventEmitter,
	Optional,
	Output,
	ViewChild,
} from '@angular/core';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { ColumnType } from '@datachain/ui-sdk/components/dc-types';
import { DxDataGridComponent } from 'devextreme-angular';
import { SelectionChangedEvent } from 'devextreme/ui/data_grid';
import { ValidationResult } from 'devextreme/ui/validator';
import { Map } from 'immutable';
import {
	BehaviorSubject,
	concat,
	filter,
	firstValueFrom,
	map,
	merge,
	Observable,
	of,
	ReplaySubject,
	takeUntil,
	tap,
	withLatestFrom,
} from 'rxjs';

import { DcIcons } from '../../../ui/app-dc.icons';
import { DcLoaders } from '../../../ui/app-loaders';
import {
	ColumnConfigErrorKey,
	ColumnConfigListError,
	ExpositionColumnConfigListEntity,
} from '../../domain/exposition-column-config-list.entity';
import {
	ExpositionColumnConfigEntity,
	ValidationStatus,
} from '../../domain/exposition-column-config.entity';
import {
	ExpositionDiagnosticError,
	ExpositionErrorKey,
} from '../../domain/exposition-diagnostic-errors.entity';
import { MoveColumnItem } from '../../domain/exposition.types';
import { ExpositionProvider } from '../../providers/exposition.provider';

type ColumnsConfigMode = 'SELECTION' | 'PARAMETER' | 'ORDER' | 'NONE';

@Component({
	selector: 'app-exposition-columns-config',
	templateUrl: './exposition-columns-config.component.html',
	styleUrls: ['./exposition-columns-config.component.scss'],
	inputs: [
		'columnConfigList',
		'isInViewMode',
		'isInAccessConfigMode',
		'columnConfigMode',
	],
	exportAs: 'ColumnsGrid',
})
export class ExpositionColumnsConfigComponent
	extends DcBaseComponent
	implements AfterViewInit
{
	public DcIcons = DcIcons;
	protected readonly DcLoaders = DcLoaders;

	protected readonly ColumnType = ColumnType;
	public selectionCount = 0;
	public filteredView = false;
	public aliasPattern = '^[a-zA-Z0-9_]*$';
	public activeCount = 1;

	// public hasValidationErrorSubject = new BehaviorSubject<boolean>(false);
	public columnListSubject =
		new BehaviorSubject<ExpositionColumnConfigListEntity>(
			ExpositionColumnConfigListEntity.build([])
		);
	public aliasUpdateSubject = new ReplaySubject<string>(1);

	public vo$: Observable<{
		isFirstLoad: boolean;
		columnConfigList: ExpositionColumnConfigListEntity;
		isInViewMode: boolean;
		activeColumnsCount: number;
		filteredColumnsCount: number;
		isInAccessConfigMode: boolean;
		columnsConfigMode: ColumnsConfigMode;
	}>;

	@Output()
	public errors = new EventEmitter<
		Map<ColumnConfigErrorKey, ColumnConfigListError>
	>();

	@Output()
	public movedElAction = new EventEmitter<MoveColumnItem>();

	@ViewChild(DxDataGridComponent)
	private readonly grid: DxDataGridComponent | null = null;

	private readonly firstLoadSubject = new BehaviorSubject<boolean>(true);

	public constructor(
		@Optional() private readonly expositionProvider: ExpositionProvider
	) {
		super();
		this.cmpId = 'expositions-columns-config';
		this.dxLocalStorageKey = 'dx.grid.expositions:columns-config';

		this.toObservable<ExpositionColumnConfigListEntity>('columnConfigList')
			.pipe(
				takeUntil(this.onDestroy$),
				tap((list) => {
					this.columnListSubject.next(list);
					this.errors.next(list.errors);
				})
			)
			.subscribe();

		this.columnListSubject
			.pipe(
				takeUntil(this.onDestroy$),
				withLatestFrom(
					this.toObservable<boolean>('isInViewMode'),
					this.toObservable<boolean>('isInAccessConfigMode')
				),
				filter(
					([_, isInViewMode, isInAccessConfigMode]) =>
						!isInViewMode && !isInAccessConfigMode
				),
				tap(([list]) => {
					if (list.errors.has(ColumnConfigErrorKey.NoActiveColumns)) {
						this.expositionProvider.addError(
							ExpositionDiagnosticError.build({
								key: ExpositionErrorKey.NoActiveColumns,
								type: 'critic',
								message: $localize`:@@exposition.diagnosticError.noActiveColumns:`,
							})
						);
					} else {
						this.expositionProvider.removeError(
							ExpositionErrorKey.NoActiveColumns
						);
					}
					if (list.errors.has(ColumnConfigErrorKey.DuplicatedAlias)) {
						this.expositionProvider.addError(
							ExpositionDiagnosticError.build({
								key: ExpositionErrorKey.DuplicatedAliasColumns,
								type: 'critic',
								message: $localize`:@@exposition.diagnosticError.duplicatedAliasColumns:`,
							})
						);
					} else {
						this.expositionProvider.removeError(
							ExpositionErrorKey.DuplicatedAliasColumns
						);
					}
					if (list.errors.has(ColumnConfigErrorKey.InvalidField)) {
						this.expositionProvider.addError(
							ExpositionDiagnosticError.build({
								key: ExpositionErrorKey.InvalidColumns,
								type: 'critic',
								message: $localize`:@@exposition.diagnosticError.invalidColumns:`,
							})
						);
					} else {
						this.expositionProvider.removeError(
							ExpositionErrorKey.InvalidColumns
						);
					}
				})
			)
			.subscribe();

		const activeColsCount$ = this.columnListSubject.pipe(
			takeUntil(this.onDestroy$),
			map((list) =>
				list.elements.reduce((acc, curr) => (curr.isActive ? acc + 1 : acc), 0)
			)
		);

		const filteredColsCount$ = this.columnListSubject.pipe(
			takeUntil(this.onDestroy$),
			tap((l) => {
				if (this.filteredView && !l.hasDuplicatedAliases && !l.hasInvalid) {
					this.filterErrors();
				}
			}),
			map((list) =>
				list.elements.reduce(
					(acc, curr) => (curr.isFiltered ? acc + 1 : acc),
					0
				)
			)
		);

		this.vo$ = combineAll({
			isFirstLoad: this.firstLoadSubject,
			isInViewMode: merge(
				of(false),
				this.toObservable<boolean>('isInViewMode')
			),
			isInAccessConfigMode: merge(
				of(false),
				this.toObservable<boolean>('isInAccessConfigMode')
			),
			activeColumnsCount: activeColsCount$,
			filteredColumnsCount: filteredColsCount$,
			columnConfigList: this.columnListSubject,
			columnsConfigMode: concat(
				of<ColumnsConfigMode>('NONE'),
				this.toObservable<ColumnsConfigMode>('columnConfigMode')
			),
		});
	}

	public ngAfterViewInit(): void {
		super.ngAfterViewInit();
		this.toObservable<boolean>('isInAccessConfigMode')
			.pipe(
				tap((isInAccessMode) => {
					if (isInAccessMode) {
						this.cmpId = 'expositions-access-columns-config';
					}
				})
			)
			.subscribe();
	}

	public async toggleActiveAllRows(): Promise<void> {
		const columnsConfig = await firstValueFrom(this.columnListSubject);

		const nextActiveState = !columnsConfig.allActive;

		const ids = columnsConfig.elements
			.toArray()
			.filter(([_, el]) => el.isActive !== nextActiveState)
			.map(([id]) => id);

		const updatedList = columnsConfig.updateIsActive(ids, nextActiveState);

		this.columnListSubject.next(updatedList);
		this.errors.next(updatedList.errors);
	}

	public async toggleFilterAllRows(): Promise<void> {
		let updatedList;
		const columnsConfig = await firstValueFrom(this.columnListSubject);
		const ids = columnsConfig.getAllIds();
		if (columnsConfig.allFilter) {
			updatedList = columnsConfig.updateIsFiltered(ids, false);
		} else {
			updatedList = columnsConfig.updateIsFiltered(ids, true);
		}
		this.columnListSubject.next(updatedList);
		this.errors.next(updatedList.errors);
	}

	public async toggleIcAllRows(): Promise<void> {
		let updatedList;
		const columnsConfig = await firstValueFrom(this.columnListSubject);
		const ids = columnsConfig.getAllIds();
		if (columnsConfig.allIc) {
			updatedList = columnsConfig.updateIsCaseSensitive(ids, false);
		} else {
			updatedList = columnsConfig.updateIsCaseSensitive(ids, true);
		}
		this.columnListSubject.next(updatedList);
		this.errors.next(updatedList.errors);
	}

	public async toggleHashAllRows(): Promise<void> {
		let updatedList;
		const columnsConfig = await firstValueFrom(this.columnListSubject);
		const ids = columnsConfig.getAllIds();
		if (columnsConfig.allHash) {
			updatedList = columnsConfig.updateHash(ids, false);
		} else {
			updatedList = columnsConfig.updateHash(ids, true);
		}
		this.columnListSubject.next(updatedList);
		this.errors.next(updatedList.errors);
	}

	public async toggleMaskAllRows(): Promise<void> {
		let updatedList;
		const columnsConfig = await firstValueFrom(this.columnListSubject);
		const ids = columnsConfig.getAllIds();
		if (columnsConfig.allHidden) {
			updatedList = columnsConfig.updateIsHidden(ids, false);
		} else {
			updatedList = columnsConfig.updateIsHidden(ids, true);
		}
		this.columnListSubject.next(updatedList);
		this.errors.next(updatedList.errors);
	}

	public toggleAllTriggered($event: MouseEvent): void {
		$event.stopPropagation();
	}

	public async updateIsActive(
		ids: Array<string>,
		checked: boolean
	): Promise<void> {
		const columnsConfig = await firstValueFrom(this.columnListSubject);
		const updatedList = columnsConfig.updateIsActive(ids, checked);
		this.columnListSubject.next(updatedList);
		this.errors.next(updatedList.errors);
	}

	public async updateIsPrimaryKey(
		row: ExpositionColumnConfigEntity,
		checked: boolean
	): Promise<void> {
		const columnsConfig = await firstValueFrom(this.columnListSubject);
		if (row.isList || row.columnType === ColumnType.Bool) {
			return;
		}
		const updatedList = columnsConfig.updateIsPrimaryKey(row.id, checked);
		this.columnListSubject.next(updatedList);
		this.errors.next(updatedList.errors);
	}

	public async updateIsFiltered(
		ids: Array<string>,
		checked: boolean
	): Promise<void> {
		const columnsConfig = await firstValueFrom(this.columnListSubject);
		const updatedList = columnsConfig.updateIsFiltered(ids, checked);
		this.columnListSubject.next(updatedList);
		this.errors.next(updatedList.errors);
	}

	public async updateIsCaseSensitive(
		ids: Array<string>,
		checked: boolean
	): Promise<void> {
		const columnsConfig = await firstValueFrom(this.columnListSubject);
		const updatedList = columnsConfig.updateIsCaseSensitive(ids, checked);
		this.columnListSubject.next(updatedList);
		this.errors.next(updatedList.errors);
	}

	public async updateIsHashed(
		ids: Array<string>,
		checked: boolean
	): Promise<void> {
		const columnsConfig = await firstValueFrom(this.columnListSubject);
		const updatedList = columnsConfig.updateHash(ids, checked);
		this.columnListSubject.next(updatedList);
		this.errors.next(updatedList.errors);
	}

	public async updateIsHidden(
		ids: Array<string>,
		checked: boolean
	): Promise<void> {
		const columnsConfig = await firstValueFrom(this.columnListSubject);
		const updatedList = columnsConfig.updateIsHidden(ids, checked);
		this.columnListSubject.next(updatedList);
		this.errors.next(updatedList.errors);
	}

	public onSelectionChange($event: SelectionChangedEvent): void {
		this.selectionCount = $event.selectedRowsData.length;
	}

	public async onGridContentReady(): Promise<void> {
		const firstLaod = await firstValueFrom(this.firstLoadSubject);

		if (firstLaod) {
			this.firstLoadSubject.next(false);
		}
	}

	public async updateDescription(
		event: ValidationResult,
		row: ExpositionColumnConfigEntity
	): Promise<void> {
		const columnsConfig = await firstValueFrom(this.columnListSubject);
		const { status, value, brokenRule } = event;
		if (status) {
			const updatedList = columnsConfig.updateDescription(
				row.id,
				value,
				status === ValidationStatus.Valid
					? ValidationStatus.Valid
					: ValidationStatus.Invalid,
				brokenRule?.message ?? null
			);
			this.columnListSubject.next(updatedList);
			this.errors.next(updatedList.errors);
		}
	}

	public updateUsedAlias($ev: string): void {
		this.aliasUpdateSubject.next($ev);
	}

	public async updateAlias(
		event: ValidationResult,
		row: ExpositionColumnConfigEntity
	): Promise<void> {
		const columnsConfig = await firstValueFrom(this.columnListSubject);
		/* if (event.isValid) {
			const updatedList = columnsConfig.updateAlias(row.id, event.value);
			this.columnListSubject.next(updatedList);
			this.errors.next(updatedList.errors);
			return;
		}
		this.errors.next(columnsConfig.errors);*/
		const { brokenRule, status, value } = event;
		if (status) {
			const updatedList = columnsConfig.updateAlias(
				row.id,
				value,
				status === ValidationStatus.Valid
					? ValidationStatus.Valid
					: ValidationStatus.Invalid,
				brokenRule?.message ?? null
			);
			this.columnListSubject.next(updatedList);
			this.errors.next(updatedList.errors);
		}
	}

	public async getSelectedColumns(): Promise<
		Array<ExpositionColumnConfigEntity>
	> {
		if (!this.grid) {
			return [];
		}
		return (await this.grid.instance.getSelectedRowsData()) as Array<ExpositionColumnConfigEntity>;
	}

	public search(searchTerm: string): void {
		if (this.grid === null) {
			return;
		}
		this.grid.instance.searchByText(searchTerm.trim());
	}

	public filterErrors(): void {
		if (!this.grid) {
			return;
		}
		if (!this.filteredView) {
			this.filteredView = true;
			this.grid.instance.filter([
				['filterField', 'contains', 'DUPLICATED'],
				'or',
				['aliasError', '<>', null],
			]);
		} else {
			this.filteredView = false;
			this.grid.instance.clearFilter();
		}
	}

	public getUpdatedList(): ExpositionColumnConfigListEntity {
		return this.columnListSubject.value;
	}

	public move(key: string, direction: 'up' | 'down' | 'top' | 'bottom'): void {
		this.movedElAction.emit({
			elemKey: key,
			direction,
		});
	}
}
